import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Sparkles, CheckCircle2, ArrowRight, Wine, GlassWater, Bell } from "lucide-react";
import api from "../lib/api";

const FALLBACK_PRICES = {
    etsen_wijnglas: 8, etsen_wijnglas_min: 6,
    etsen_whiskyglas: 12, etsen_whiskyglas_min: 4,
    etsen_stolp: 25, etsen_stolp_min: 1,
};

const USE_CASES = [
    { icon: Wine,        title: "Wijnglazen",          desc: "Naam, datum of monogram op het glas — perfect voor verjaardagen of huwelijken." },
    { icon: GlassWater,  title: "Whiskyglazen",        desc: "Persoonlijke initialen of jaartal op tumbler-glazen — premium cadeau voor de liefhebber." },
    { icon: Bell,        title: "Glazen stolpen",      desc: "Bedrijfslogo, familie-naam of slogan op een glazen stolp of vitrinekap." },
];

export default function EtsenOpGlasPage() {
    const [prices, setPrices] = useState(FALLBACK_PRICES);
    useEffect(() => {
        api.get("/prices")
            .then(({ data }) => setPrices(p => ({ ...p, ...data })))
            .catch(() => {});
    }, []);

    const fmt = (n) => `€ ${Number(n).toFixed(0)}`;

    return (
        <div data-testid="etsen-page" className="min-h-screen" style={{ background: "var(--bg)" }}>
            {/* HERO */}
            <section className="hero-warm border-b border-[var(--border-soft)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Etsen op glaswerk</p>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-3 leading-[1.05] tracking-tight">
                            Etsen op glas.<br />
                            <span className="font-script text-5xl md:text-6xl lg:text-7xl" style={{ color: "var(--rose-deep)" }}>Met jouw naam.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--text-2)" }}>
                            Personaliseer <strong>wijnglazen</strong>, <strong>whiskyglazen</strong> en <strong>glazen stolpen</strong>
                            met een naam, datum, initialen of klein logo. De etsing is permanent en vaatwasserbestendig —
                            een tijdloos cadeau dat blijft.
                        </p>

                        {/* OP BESTELLING NOTICE — prominent */}
                        <div className="mt-6 inline-flex items-start gap-2.5 p-3.5 rounded-2xl border max-w-xl" style={{ background: "var(--rose-tint)", borderColor: "var(--rose-soft)" }} data-testid="etsen-orderonly-notice">
                            <Clock size={18} style={{ color: "var(--rose-deep)" }} className="mt-0.5 shrink-0" />
                            <div>
                                <p className="text-sm leading-relaxed" style={{ color: "var(--text)" }}>
                                    <strong>Enkel op bestelling</strong> — productietijd 3-7 werkdagen.
                                </p>
                                <p className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                                    Enkel mogelijk op wijnglazen, whiskyglazen en glazen stolpen. Vraag eerst een offerte via &laquo;Op maat&raquo;.
                                </p>
                            </div>
                        </div>

                        <div className="mt-7 flex flex-wrap gap-3 items-center">
                            <Link to="/maak-voor-mij" data-testid="etsen-cta-quote" className="btn-rose">
                                <Sparkles size={14} /> Vraag offerte aan <ArrowRight size={14} />
                            </Link>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent("Hallo VdS Gift Shop, ik wil graag een offerte voor etsen op glas. Ik zoek: ")}`}
                                target="_blank" rel="noopener noreferrer"
                                data-testid="etsen-cta-whatsapp"
                                className="btn-ghost inline-flex items-center gap-2">
                                <span className="text-base" aria-hidden>💬</span> Vraag via WhatsApp
                            </a>
                        </div>

                        {/* USPs */}
                        <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 text-xs max-w-md" style={{ color: "var(--text-2)" }}>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Permanent &amp; vaatwasserbestendig</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Subtiel mat-effect</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Eigen tekst of monogram</span>
                            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Ideaal als cadeau</span>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative min-h-[420px]">
                        <div className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(62,39,35,0.18)]">
                            <img
                                src="https://images.unsplash.com/photo-1547595628-c61a29f496f0?auto=format&fit=crop&w=900&q=80"
                                alt="Gepersonaliseerd wijnglas met etsing"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = "https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&cs=tinysrgb&w=900"; }}
                            />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(62,39,35,0.18) 100%)" }} />
                        </div>
                        <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur px-4 py-3 rounded-2xl shadow-[0_8px_24px_rgba(62,39,35,0.12)]" data-testid="etsen-floating-tag">
                            <p className="font-script text-xl leading-tight" style={{ color: "var(--rose-deep)" }}>Gepersonaliseerd</p>
                            <p className="label-tiny mt-0.5">Wijn · whisky · stolpen</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Wat we etsen</p>
                    <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                        Drie soorten glaswerk, <span className="font-script" style={{ color: "var(--rose-deep)" }}>eindeloos persoonlijk.</span>
                    </h2>
                    <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
                        We etsen <strong>alleen</strong> op deze items — andere materialen (ramen, douchewanden, gevels) zijn jammer genoeg niet mogelijk.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
                    {USE_CASES.map((u, i) => {
                        const Icon = u.icon;
                        return (
                            <div key={i} data-testid={`etsen-usecase-${i}`} className="card-soft bg-white p-6">
                                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--rose-tint)" }}>
                                    <Icon size={22} style={{ color: "var(--rose-deep)" }} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-display text-xl mt-4 leading-tight" dangerouslySetInnerHTML={{ __html: u.title }} />
                                <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-2)" }}>{u.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* PRIJSINDICATIE */}
            <section className="border-t border-[var(--border-soft)]" style={{ background: "var(--bg)" }}>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
                    <div className="text-center max-w-xl mx-auto mb-10">
                        <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Vanaf-prijzen</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                            Wat kost een <span className="font-script" style={{ color: "var(--rose-deep)" }}>gepersonaliseerd glas?</span>
                        </h2>
                        <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
                            Indicatieve vanaf-prijzen per stuk, incl. glas, ontwerp &amp; ets-bewerking. Definitieve prijs in offerte (afhankelijk van complexiteit &amp; aantal).
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto" data-testid="etsen-pricing">
                        <PriceCard
                            label="Wijnglas"
                            price={fmt(prices.etsen_wijnglas)}
                            sub={`per stuk · vanaf ${prices.etsen_wijnglas_min} glazen`}
                            note="Naam, initialen of datum"
                            accent="rose-tint"
                            testId="price-wijnglas"
                        />
                        <PriceCard
                            label="Whiskyglas"
                            price={fmt(prices.etsen_whiskyglas)}
                            sub={`per stuk · vanaf ${prices.etsen_whiskyglas_min} glazen`}
                            note="Tumbler met monogram"
                            accent="gold-soft"
                            testId="price-whiskyglas"
                            highlighted
                        />
                        <PriceCard
                            label="Glazen stolp"
                            price={fmt(prices.etsen_stolp)}
                            sub={`per stuk · vanaf ${prices.etsen_stolp_min} stolp${prices.etsen_stolp_min === 1 ? "" : "en"}`}
                            note="Logo, naam of slogan"
                            accent="bg-warm"
                            testId="price-stolp"
                        />
                    </div>

                    <p className="text-[11px] mt-6 text-center" style={{ color: "var(--text-3)" }}>
                        Eigen glaswerk meebrengen? Bespreek het in je aanvraag — vaak mogelijk mits compatibiliteits-check.
                    </p>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-t border-[var(--border-soft)]" style={{ background: "var(--bg-warm)" }}>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
                    <div className="text-center max-w-xl mx-auto mb-10">
                        <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Zo werkt het</p>
                        <h2 className="font-display text-4xl mt-3">Van idee tot geplakt.</h2>
                    </div>
                    <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <Step n="01" title="Stuur je ontwerp" desc="Naam, datum, initialen of klein logo. Geef ook door welk glaswerk: wijn, whisky of stolp + hoeveel stuks." />
                        <Step n="02" title="Wij maken offerte" desc="Binnen 24 uur krijg je prijs, levertijd en een digitale preview ter goedkeuring." />
                        <Step n="03" title="Wij etsen &amp; leveren" desc="Productie in 3-7 werkdagen. Permanent — vaatwasser-veilig. Klaar om cadeau te geven." />
                    </ol>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 text-center">
                <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Klaar om te beginnen?</p>
                <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                    Vraag <span className="font-script" style={{ color: "var(--rose-deep)" }}>vrijblijvend</span> offerte aan.
                </h2>
                <p className="mt-4 max-w-xl mx-auto text-sm" style={{ color: "var(--text-2)" }}>
                    Beschrijf je idee en de afmetingen — wij sturen je binnen 24 uur een voorstel met digitale preview.
                </p>
                <div className="mt-7 flex justify-center gap-3">
                    <Link to="/maak-voor-mij" data-testid="etsen-bottom-cta" className="btn-rose">
                        <Sparkles size={14} /> Offerte aanvragen
                    </Link>
                </div>
            </section>
        </div>
    );
}

function Step({ n, title, desc }) {
    return (
        <li className="rounded-2xl bg-white p-6 border border-[var(--border-soft)]">
            <span className="label-tiny" style={{ color: "var(--rose-deep)" }}>Stap {n}</span>
            <h3 className="font-display text-xl mt-2 leading-tight" dangerouslySetInnerHTML={{ __html: title }} />
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-2)" }} dangerouslySetInnerHTML={{ __html: desc }} />
        </li>
    );
}

function PriceCard({ label, price, sub, note, accent, testId, highlighted }) {
    const bgMap = { "rose-tint": "var(--rose-tint)", "gold-soft": "var(--gold-soft)", "bg-warm": "var(--bg-warm)" };
    return (
        <div
            data-testid={testId}
            className={`relative rounded-3xl p-7 transition-all ${highlighted ? "scale-[1.03] shadow-[0_12px_36px_rgba(62,39,35,0.10)]" : "shadow-[0_4px_14px_rgba(62,39,35,0.05)]"}`}
            style={{ background: bgMap[accent] || "var(--rose-tint)" }}>
            {highlighted && (
                <span className="absolute -top-2.5 right-5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase" style={{ background: "var(--rose-deep)", color: "white" }}>
                    Populair
                </span>
            )}
            <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>{label}</p>
            <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-[10px] font-mono-dm uppercase tracking-widest" style={{ color: "var(--text-2)" }}>vanaf</span>
                <span className="font-display text-5xl leading-none" style={{ color: "var(--text)" }}>{price}</span>
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{sub}</p>
            <p className="text-sm mt-3" style={{ color: "var(--text)" }}>{note}</p>
        </div>
    );
}
